import { type MouseEvent, useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { GetProgressUpdatesOverview, GetTemplateInstructions } from '../../../../services/CETAClassroomService'
import { type ProgressUpdatesOverviewSection, type ProgressUpdatesOverviewSectionCardModel } from '../../../../models/ProgressUpdatesOverviewSectionCardModel'
import { toast } from 'react-toastify'
import { AppRoutes } from '../../../Routes'
import { CETAProgressUpdateSection } from './CETAProgressUpdateSection'
import { Box, Card, Grid, type SxProps, Typography } from '@mui/material'
import { CETAInstructionsKey } from '../../../../models/CETATemplateInstructions'
import { BlockButton } from '../../../customComponents/buttons/BlockButton'
import { BlueButton } from '../../../customComponents/buttons/BlueButton'
import { AddCircleOutline, CheckCircleOutline, Close, Download } from '@mui/icons-material'
import { CETAOverviewTabs } from '../CETAOverview'

interface Props {
  readOnly: boolean
  classroomId: string
  viewingProgressUpdateDetails: boolean
  setViewingProgressUpdateDetails: (viewingDetails: boolean) => void
}

export const CETAProgressUpdatesOverview = (props: Props): JSX.Element => {
  const nav = useNavigate()

  const [cards, setCards] = useState<ProgressUpdatesOverviewSectionCardModel[]>([])
  const [progressUpdateInstructions, setProgressUpdateInstructions] = useState('')
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null)
  const [isSelectingSections, setIsSelectingSections] = useState<boolean>(false)

  const fetchSections = async (): Promise<void> => {
    const overviewResponse = await GetProgressUpdatesOverview(props.classroomId)
    const cardModel: ProgressUpdatesOverviewSectionCardModel[] = []
    overviewResponse.forEach(r => cardModel.push({ section: r, isSelected: false }))
    setCards(cardModel)
  }

  const fetchInstructions = async (): Promise<void> => {
    const instructionResponse = await GetTemplateInstructions(props.classroomId)
    setProgressUpdateInstructions(instructionResponse.progressUpdate)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await Promise.all([
        fetchSections(),
        fetchInstructions()
      ])
    }

    void fetchData()
  }, [])

  const handleViewDetails = (e: MouseEvent, section: ProgressUpdatesOverviewSection): void => {
    e.stopPropagation()
    props.setViewingProgressUpdateDetails(true)
    setSelectedSectionId(section.id)
  }

  const handleBack = (): void => {
    props.setViewingProgressUpdateDetails(false)
    setSelectedSectionId(null)
  }

  const selectCard = (id: number): void => {
    if (!isSelectingSections) {
      return
    }

    const copiedCards: ProgressUpdatesOverviewSectionCardModel[] = [...cards]

    copiedCards.forEach(c => {
      if (c.section.id === id) {
        c.isSelected = !c.isSelected
      }
    })

    setCards(copiedCards)
  }

  const printSelection = (): void => {
    if (cards.find(c => c.isSelected) == null) {
      toast.error('No sections are selected.')
      return
    }
    const sectionsToPrint: string[] = cards.filter((c) => c.isSelected).map(c => c.section.id.toString())
    const printRoute = generatePath(AppRoutes.CETA_SECTION_PRINT, { classroomId: props.classroomId, tab: CETAOverviewTabs.ProgressUpdates, sectionIds: sectionsToPrint.join(',') })
    nav(printRoute)
  }

  return <>
    {!props.viewingProgressUpdateDetails &&
      <Grid container spacing={2}>
        <Grid item width='65%'>
          <Box sx={{ display: 'flex', marginLeft: '2em', justifyContent: 'space-between' }}>
            <Typography variant='h3'>Sections</Typography>

            {isSelectingSections &&
              <Box>
                <BlueButton onClick={() => { setIsSelectingSections(false) }} sx={{ marginLeft: '1em', justifyContent: 'space-between' }} dataTestId='cancelSelection'>
                  Cancel
                  <Close sx={{ ml: '.25em' }} />
                </BlueButton>

                <BlueButton onClick={printSelection} sx={{ marginLeft: '1em', justifyContent: 'space-between' }} dataTestId='downloadGoals'>
                  Download Selected Sections
                  <Download sx={{ ml: '.25em' }} />
                </BlueButton>
              </Box>
            }

            {!isSelectingSections &&
              <BlueButton onClick={() => { setIsSelectingSections(true) }} sx={{ marginLeft: '1em', justifyContent: 'space-between' }} dataTestId='setSections'>
                Select sections for Action Plan
                <Download sx={{ ml: '.25em' }} />
              </BlueButton>
            }
          </Box>

          {isSelectingSections &&
            <Typography variant='body1' sx={{ marginLeft: '2em' }}>Select the sections you'd like to generate an action plan for</Typography>
          }

          <Grid container spacing={2} sx={{ mt: '1em', ml: '2em' }}>
            {cards.map((card, index) => {
              const backGroundColor: string = index % 2 === 0 ? 'var(--light-blue-100)' : 'var(--purple-100)'
              const selectedColor: string = index % 2 === 0 ? 'var(--blue-700)' : 'var(--purple-700)'
              const hoverColor: string = index % 2 === 0 ? 'rgb(150, 201, 219)' : 'rgb(188, 189, 206)'
              const fillColor: string = card.isSelected ? selectedColor : 'var(--grey-700)'
              const iconSx: SxProps = { cursor: 'pointer', position: 'relative', left: 0, top: 20, zIndex: 1, backgroundColor: 'white', borderRadius: '16px', fill: fillColor }

              return <Grid item key={index}>
                <Box>
                  {isSelectingSections && card.isSelected &&
                    <CheckCircleOutline
                      data-testid={`${card.section.name}-checkIcon`}
                      onClick={() => { selectCard(card.section.id) }}
                      sx={iconSx}
                    />
                  }

                  {isSelectingSections && !card.isSelected &&
                    <AddCircleOutline
                      data-testid={`${card.section.name}-addIcon`}
                      onClick={() => { selectCard(card.section.id) }}
                      sx={iconSx}
                    />
                  }

                  <Card
                    onClick={() => { selectCard(card.section.id) }}
                    data-testid={`${card.section.name}-card`}
                    sx={{
                      position: 'relative',
                      width: '270px',
                      height: '270px',
                      borderRadius: '16px',
                      backgroundColor: backGroundColor,
                      ':hover': isSelectingSections ? { cursor: 'pointer', backgroundColor: hoverColor } : {},
                      border: !isSelectingSections ? '' : card.isSelected ? `6px solid ${selectedColor}` : '6px solid var(--grey-100)'
                    }}
                  >
                    <Box sx={{ margin: '1em' }}>
                      <Typography variant='h4'>{card.section.name}</Typography>

                      <Box sx={{ marginTop: '3em' }}>
                        {!isSelectingSections &&
                          <BlockButton
                            sx={{ position: 'absolute', bottom: 0, marginBottom: '1em' }}
                            backgroundColor='var(--grey-100)'
                            onClick={(e) => { handleViewDetails(e, card.section) }}
                            dataTestId={card.section.name + '-detailsButton'}
                          >
                            <Typography variant='h5' fontSize='14px' color={'var(--grey-700)'}>
                              View Section Details
                            </Typography>
                          </BlockButton>
                        }
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Grid>
            })}
          </Grid>
        </Grid>

        <Grid item>
          <Card
            sx={{
              backgroundColor: 'var(--off-white)',
              maxWidth: '350px',
              border: '1px solid var(--blue-300)',
              p: '1em'
            }}
          >
            <Typography variant='h5' color={'var(--blue-700)'}>
              {CETAInstructionsKey.ProgressUpdateInstructions}
            </Typography>

            <div className='ql-editor' dangerouslySetInnerHTML={{ __html: progressUpdateInstructions }} />
          </Card>
        </Grid>
      </Grid>
    }

    {props.viewingProgressUpdateDetails && selectedSectionId != null &&
      <CETAProgressUpdateSection
        readOnly={props.readOnly}
        classroomId={props.classroomId}
        sectionId={selectedSectionId}
        goBack={handleBack}
        refetchOverviewSections={fetchSections}
      />
    }
  </>
}
